import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { IconButton } from '@/components';

import type { GradientFill, GradientStop } from '../styleTypes';

import { ColorPickerAdvanced } from './ColorPickerAdvanced';
import { NumberInput } from './NumberInput';
import { StyleToggleButton } from './StyleToggleButton';

interface GradientEditorProps {
  gradient: GradientFill | undefined;
  onChange: (gradient: GradientFill | undefined) => void;
}

const DEFAULT_GRADIENT: GradientFill = {
  type: 'linear',
  angle: 180,
  stops: [
    { color: '#3B82F6', position: 0 },
    { color: '#8B5CF6', position: 100 },
  ],
};

export function GradientEditor({ gradient, onChange }: GradientEditorProps) {
  const { t } = useTranslation();
  const g = gradient || DEFAULT_GRADIENT;

  const updateStop = (index: number, updates: Partial<GradientStop>) => {
    const current = g.stops[index];
    if (!current) return;
    const stops = [...g.stops];
    stops[index] = { ...current, ...updates };
    onChange({ ...g, stops });
  };

  const addStop = () => {
    if (g.stops.length >= 6) return;
    const stops = [...g.stops, { color: '#10B981', position: 50 }];
    onChange({ ...g, stops });
  };

  const removeStop = (index: number) => {
    if (g.stops.length <= 2) return;
    const stops = g.stops.filter((_, i) => i !== index);
    onChange({ ...g, stops });
  };

  const gradientPreview = () => {
    const stops = [...g.stops]
      .sort((a, b) => a.position - b.position)
      .map((s) => `${s.color} ${s.position}%`)
      .join(', ');
    if (g.type === 'radial') return `radial-gradient(circle, ${stops})`;
    return `linear-gradient(${g.angle ?? 180}deg, ${stops})`;
  };

  return (
    <div className="space-y-3">
      {/* Preview */}
      <div
        className="w-full h-10 rounded-lg border border-gray-200 dark:border-gray-600"
        style={{ background: gradientPreview() }}
      />

      {/* Type + Angle — `flex-wrap`: gdy panel jest za wąski żeby zmieścić oba obok
          siebie, „Kąt" spada pod „Typ" zamiast być ucinany przez prawą krawędź. */}
      <div className="flex flex-wrap gap-2">
        <div className="min-w-[7rem] flex-1">
          <label className="block text-[10px] text-gray-400 mb-0.5">
            {t('tenant.systemModuleBuilder.style.gradient.type')}
          </label>
          <div className="flex gap-1">
            {(['linear', 'radial'] as const).map((type) => (
              <StyleToggleButton
                key={type}
                active={g.type === type}
                onClick={() => onChange({ ...g, type })}
                className="truncate"
              >
                {type === 'linear'
                  ? t('tenant.systemModuleBuilder.style.gradient.linear')
                  : t('tenant.systemModuleBuilder.style.gradient.radial')}
              </StyleToggleButton>
            ))}
          </div>
        </div>
        {g.type === 'linear' && (
          <NumberInput
            value={g.angle}
            onChange={(v) => onChange({ ...g, angle: v })}
            min={0}
            max={360}
            unit="°"
            label={t('tenant.systemModuleBuilder.style.gradient.angle')}
            className="w-24"
          />
        )}
      </div>

      {/* Stops */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-400 uppercase tracking-wider">
            {t('tenant.systemModuleBuilder.style.gradient.colorStops')}
          </span>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t('tenant.systemModuleBuilder.style.gradient.addStop')}
            onClick={addStop}
            disabled={g.stops.length >= 6}
            className="text-accent-500 hover:text-accent-600"
          >
            <Plus className="w-3.5 h-3.5" />
          </IconButton>
        </div>
        {g.stops.map((stop, i) => (
          <div key={i} className="flex items-center gap-2">
            <ColorPickerAdvanced
              color={stop.color}
              onChange={(c) => updateStop(i, { color: c || '#000' })}
              allowClear={false}
              compact
            />
            <NumberInput
              value={stop.position}
              onChange={(v) => updateStop(i, { position: v ?? 0 })}
              min={0}
              max={100}
              unit="%"
              className="min-w-0 flex-1"
            />
            {g.stops.length > 2 && (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label={t('tenant.systemModuleBuilder.style.gradient.removeStop')}
                onClick={() => removeStop(i)}
                className="shrink-0 text-gray-400 hover:text-red-500"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </IconButton>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
